import React, { useState } from 'react';
import { Save, RefreshCw } from 'lucide-react';

const PARAM_LABELS = {
  dias_nomina:          { label: 'Días de Nómina',        desc: 'Días base para cálculo mensual'                    },
  decimales:            { label: 'Decimales',             desc: 'Cifras decimales en montos'                        },
  tipo_nomina:          { label: 'Tipo de Nómina',        desc: 'Frecuencia de pago'                                },
  moneda:               { label: 'Moneda',                desc: 'Moneda para cálculos'                              },
  infotep_bonificacion: { label: 'INFOTEP Bonificación',  desc: 'Tasa INFOTEP sobre bonificación anual (%) — solo bonificaciones, no nómina regular' },
};

// Controles específicos por parámetro en lugar del input de texto genérico
const PARAM_CONTROLS = {
  tipo_nomina: {
    type: 'select',
    options: [
      { value: 'quincenal', label: 'Quincenal' },
      { value: 'mensual',   label: 'Mensual'   }
    ]
  },
  moneda: {
    type: 'select',
    options: [
      { value: 'DOP', label: 'DOP — Peso Dominicano' },
      { value: 'USD', label: 'USD — Dólar Americano' }
    ]
  },
  dias_nomina:          { type: 'number', min: 1,   max: 31  },
  decimales:            { type: 'number', min: 0,   max: 4   },
  infotep_bonificacion: { type: 'number', min: 0,   max: 100, step: 0.01 }
};

function ParamControl({ parametro, value, onChange }) {
  const ctrl = PARAM_CONTROLS[parametro];
  const base = 'px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none';

  if (ctrl?.type === 'select') {
    return (
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-52 ${base}`}
      >
        {ctrl.options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    );
  }

  if (ctrl?.type === 'number') {
    return (
      <input
        type="number"
        min={ctrl.min}
        max={ctrl.max}
        step={ctrl.step ?? 1}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-24 text-right ${base}`}
      />
    );
  }

  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`w-40 ${base}`}
    />
  );
}

export default function ParametrosTab({ parametros, saveParametros }) {
  const [editData, setEditData] = useState(() =>
    Object.fromEntries(parametros.map(p => [p.parametro, p.valor]))
  );
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleChange = (key, value) => {
    setEditData(prev => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = Object.entries(editData).map(([parametro, valor]) => ({
        parametro,
        valor,
        descripcion: PARAM_LABELS[parametro]?.desc || null
      }));
      await saveParametros(payload);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      alert('Error al guardar: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  if (parametros.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <RefreshCw className="w-8 h-8 mx-auto mb-2 opacity-40" />
        <p>No hay parámetros configurados</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="overflow-hidden border border-gray-200 rounded-xl">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-bold uppercase text-gray-600">Parámetro</th>
              <th className="px-6 py-3 text-left text-xs font-bold uppercase text-gray-600">Descripción</th>
              <th className="px-6 py-3 text-left text-xs font-bold uppercase text-gray-600">Valor</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {parametros.map(p => (
              <tr key={p.parametro} className="hover:bg-gray-50 transition-colors">
                <td className="px-6 py-4">
                  <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded text-gray-700">
                    {p.parametro}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-600">
                  {PARAM_LABELS[p.parametro]?.desc || p.descripcion || '—'}
                </td>
                <td className="px-6 py-4">
                  <ParamControl
                    parametro={p.parametro}
                    value={editData[p.parametro] ?? p.valor}
                    onChange={(v) => handleChange(p.parametro, v)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors font-semibold"
        >
          {saving ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : saved ? (
            <span>Guardado</span>
          ) : (
            <Save className="w-4 h-4" />
          )}
          {saving ? 'Guardando...' : saved ? 'Guardado' : 'Guardar Parámetros'}
        </button>
      </div>
    </div>
  );
}
